import productTrackingMapping from 'root/utils/tracking/GA4/productTrackingMapping.json';
import type {
    GTMEvent,
    incompleteGTMEvent,
} from 'root/utils/tracking/GA4/googleTagManager';
import { PlacementEnums } from 'root/utils/tracking/GA4/types';

type Field = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
type FormEventAction =
    | 'form_submit'
    | 'form_success'
    | 'form_error'
    | 'form_in_page'
    | 'form_lightbox_open'
    | 'form_lightbox_closed'
    | 'form_email_opt_in'
    | 'form_email_opt_out'
    | 'field_completed'
    | 'field_autocompleted'
    | 'field_validation_error'
    | 'field_displayed';

/*
 * Event actions that describe a submission attempt and its outcome. Params
 * gated on this list belong to the submission itself, not to a single field.
 */
const FORM_SUBMISSION_EVENT_ACTIONS: FormEventAction[] = [
    'form_submit',
    'form_success',
    'form_error',
];

class FormGTMObjectConstructor {
    #form: HTMLFormElement;
    #fields: Field[];
    #incompleteFormGTMObject: incompleteGTMEvent;
    #isGolfProduct: boolean;

    constructor(form: HTMLFormElement) {
        this.#form = form;
        this.#fields = Array.from(form.elements).filter(element => {
            return 'type' in element ? element.type !== 'button' : false;
        }) as Field[];
        this.#isGolfProduct = this.#getFieldValue('product')?.toLowerCase() === 'golf';
        this.#incompleteFormGTMObject = {};
    }
    #getFieldValue(fieldName: string, isHidden = true) {
        if (isHidden) {
            return this.#fields.find(field => field.name === fieldName)?.value || null;
        }
        return (
            this.#fields.find(
                field => field.type !== 'hidden' && field.name === fieldName
            )?.value || null
        );
    }

    #getFormNumberOfSteps(): string {
        return getFormNumberOfSteps(this.#form);
    }

    #getFormPlacement(): 'in-lightbox' | 'in-page' {
        return getFormPlacement(this.#form);
    }

    #getFormLayoutType(): string {
        return getFormLayoutType(this.#form);
    }

    #getFormLayoutTypeVersion(): string {
        return getFormLayoutTypeVersion(this.#form);
    }

    #getTrackingValue(
        trackingKey: string,
        eventAction: FormEventAction,
        completedFieldValue?: string,
        fieldName?: string
    ) {
        switch (trackingKey) {
            case 'form_name': {
                const fieldValue = this.#getFieldValue('form_name');
                return this.#form.dataset.formTrackingName ?? fieldValue;
            }
            case 'form_number_of_steps':
                return this.#getFormNumberOfSteps();
            case 'form_placement':
                return this.#getFormPlacement();
            case 'form_layout_type':
                return this.#getFormLayoutType();
            case 'form_layout_type_version':
                return this.#getFormLayoutTypeVersion();
            case 'product_type': {
                /* This is used for non-wizard agnostic forms where the product can be chosen from the select field */
                const productValueFromSelectField =
                    this.#getFieldValue('product', false)?.toLowerCase() ?? null;

                /* This is used for non-wizard product-related forms (Retail, or Resto, or Golf) */
                const productValueFromHiddenInputField =
                    this.#getFieldValue('product')?.toLowerCase() ?? null;

                const productFieldValue =
                    productValueFromSelectField || productValueFromHiddenInputField;

                const mappingObject: Record<string, string> = productTrackingMapping;

                let productValue;
                if (productFieldValue) {
                    productValue = mappingObject[productFieldValue] ?? productFieldValue;
                }

                const blogProductFieldValue = this.#getFieldValue('blog_product')
                    ?.split('.')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(', ');

                return productValue ?? blogProductFieldValue;
            }
            case 'stage':
                return this.#getFieldValue('stage');
            case 'role':
                if (!['form_success', 'form_submit'].includes(eventAction)) {
                    return null;
                }

                return this.#isGolfProduct && this.#getFieldValue('role');
            case 'business_type':
                if (
                    this.#isGolfProduct ||
                    !FORM_SUBMISSION_EVENT_ACTIONS.includes(eventAction)
                ) {
                    return null;
                }
                return (
                    this.#getFieldValue('resto_business_type') ??
                    this.#getFieldValue('agnostic_business_type') ??
                    this.#getFieldValue('business_type') ??
                    this.#getFieldValue('business_type_slug')
                        ?.split('_')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ')
                );
            case 'secondary_business_type':
                if (!FORM_SUBMISSION_EVENT_ACTIONS.includes(eventAction)) {
                    return null;
                }
                /*
                 * The submitted value ("Other Services"), so this param matches the
                 * field_value of the field_completed event for the same selection.
                 */
                return this.#getFieldValue('secondary_business_type');
            case 'golf_course_type':
                if (!['form_success', 'form_submit'].includes(eventAction)) {
                    return null;
                }
                return this.#isGolfProduct
                    ? this.#getFieldValue('golf_course_type')
                    : null;
            case 'gtv_value':
                if (!['form_success', 'form_submit'].includes(eventAction)) {
                    return null;
                }
                if (this.#isGolfProduct) {
                    return null;
                }
                return this.#getFieldValue('estimated_annual_gtv');
            case 'field_value':
                return completedFieldValue;

            case 'field_name':
                return fieldName;
            case 'internal_test': {
                const emailValue = this.#getFieldValue('email') ?? '';
                if (emailValue && eventAction === 'form_success') {
                    return emailValue
                        ?.split('@')[1]
                        ?.match(/lightspeedhq\.[a-z]{0,4}(\.[a-z]{0,4})?/)
                        ? '1'
                        : '0';
                }
                break;
            }
            case 'placement':
                return getFormPlacementFromDataAttribute(this.#form);
        }
    }
    getFormGTMObject(
        eventAction: FormEventAction,
        completedFieldValue?: string,
        fieldName?: string
    ): GTMEvent {
        const trackingKeys = [
            'form_name',
            'form_number_of_steps',
            'form_placement',
            'form_layout_type',
            'form_layout_type_version',
            'product_type',
            'stage',
            'role',
            'business_type',
            'secondary_business_type',
            'golf_course_type',
            'gtv_value',
            'field_value',
            'field_name',
            'internal_test',
            'placement',
        ];

        // Here we loop through each trackingKey (E.g. 'business_type') that we want to populate and send to Google Analytics
        for (const trackingKey of trackingKeys) {
            const trackingValue = this.#getTrackingValue(
                trackingKey,
                eventAction,
                completedFieldValue,
                fieldName
            );
            if (trackingValue) {
                this.#incompleteFormGTMObject[trackingKey] = trackingValue;
            }
        }

        return Object.assign(
            { event_action: eventAction },
            this.#incompleteFormGTMObject
        );
    }
}

const getPlacement = (element: HTMLElement) => {
    if (element.closest('.footer-main')) {
        return PlacementEnums.footer;
    }
    if (element.closest('.main-site-header-btf')) {
        return PlacementEnums.nav;
    }
    if (element.closest('[data-hero-section]')) {
        return PlacementEnums.hero;
    }
    return PlacementEnums.body;
};

const getFormPlacementFromDataAttribute = (form: HTMLFormElement) => {
    return form.dataset.formPlacement ?? getPlacement(form);
};

/**
 * Total number of steps the user sees, read from the data-tracking-form-number-of-steps
 * attribute (derived once in LeadForm.php getNumberOfSteps, emitted via Twig).
 * Defaults to '1' when absent.
 */
const getFormNumberOfSteps = (form: HTMLFormElement): string =>
    form.dataset.trackingFormNumberOfSteps ?? '1';

/**
 * The form_placement GTM param: whether the form renders in a lightbox or in the page,
 * determined by an ancestor .js-lightbox-wrapper lookup (lightbox forms are injected inside it).
 * Note this is unrelated to the existing data-form-placement attribute (read by
 * getFormPlacementFromDataAttribute), which feeds the separate `placement` param.
 */
const getFormPlacement = (form: HTMLFormElement): 'in-lightbox' | 'in-page' =>
    form.closest('.js-lightbox-wrapper') ? 'in-lightbox' : 'in-page';

/**
 * The layout shape (standard, wizard or sandbox), read from the data-tracking-form-layout-type
 * attribute (set from the form config). Defaults to 'standard'.
 */
const getFormLayoutType = (form: HTMLFormElement): string =>
    form.dataset.trackingFormLayoutType ?? 'standard';

/**
 * The layout version, read from the data-tracking-form-layout-type-version attribute
 * (set from the form config). Defaults to 'v1'.
 */
const getFormLayoutTypeVersion = (form: HTMLFormElement): string =>
    form.dataset.trackingFormLayoutTypeVersion ?? 'v1';

export {
    FormGTMObjectConstructor,
    getPlacement,
    getFormPlacementFromDataAttribute,
    getFormNumberOfSteps,
    getFormPlacement,
    getFormLayoutType,
    getFormLayoutTypeVersion,
};
